import { HttpInterceptorFn, HttpResponse, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { finalize, tap } from 'rxjs';

// Tokens to customize interceptor behavior per-request if needed
export const BYPASS_SPINNER = new HttpContextToken<boolean>(() => false);
export const BYPASS_TOASTER = new HttpContextToken<boolean>(() => false);

let activeRequestsCount = 0;
let safetyTimeoutId: any = null;

/**
 * Forcefully reset the spinner state — called as a safety net
 * to prevent the spinner overlay from getting stuck and blocking clicks.
 */
function forceHideSpinner(spinner: NgxSpinnerService, zone: NgZone): void {
  activeRequestsCount = 0;
  zone.run(() => {
    spinner.hide();
  });

  // Double-check: physically remove any stuck overlay elements after a short delay
  setTimeout(() => {
    const overlays = document.querySelectorAll('ngx-spinner .overlay');
    overlays.forEach((el: Element) => {
      (el as HTMLElement).style.display = 'none';
      (el as HTMLElement).style.pointerEvents = 'none';
    });
  }, 100);
}

export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  const spinner = inject(NgxSpinnerService);
  const toastr = inject(ToastrService);
  const zone = inject(NgZone);

  // Check if request is a background call (e.g. loading images, assets) or explicitly bypassed
  const isBackgroundCall = req.url.includes('/assets/') ||
                           req.url.startsWith('assets/') ||
                           req.url.includes('/getImage/') ||
                           req.url.includes('/image/') ||
                           req.url.includes('/get-profile-image/') ||
                           req.url.endsWith('.png') ||
                           req.url.endsWith('.jpg') ||
                           req.url.endsWith('.jpeg') ||
                           req.url.endsWith('.gif') ||
                           req.url.endsWith('.svg') ||
                           req.url.endsWith('.json') ||
                           req.context.get(BYPASS_SPINNER);

  if (!isBackgroundCall) {
    activeRequestsCount++;
    if (activeRequestsCount === 1) {
      zone.run(() => {
        spinner.show();
      });

      // Safety timeout: force-hide spinner after 8 seconds to prevent
      // the overlay from getting stuck and blocking all user interactions
      if (safetyTimeoutId) {
        clearTimeout(safetyTimeoutId);
      }
      safetyTimeoutId = setTimeout(() => {
        if (activeRequestsCount > 0) {
          console.warn('Loading spinner safety timeout triggered — forcing hide.');
          forceHideSpinner(spinner, zone);
        }
        safetyTimeoutId = null;
      }, 8000);
    }
  }

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          // Toast success on modify requests (POST, PUT, DELETE) if not bypassed
          if (!req.context.get(BYPASS_TOASTER) && ['POST', 'PUT', 'DELETE'].includes(req.method)) {
            // Skip toasts for authentication endpoints if they don't return message
            const isAuth = req.url.includes('/auth/login') || req.url.includes('/auth/register');
            const body = event.body as any;
            const successMsg = body?.msg || body?.message || body?.msgText;

            if (successMsg) {
              toastr.success(successMsg);
            } else if (!isAuth) {
              // Fallback generic messages
              if (req.method === 'DELETE' || req.url.includes('/delete')) {
                toastr.success('Deleted successfully');
              } else if (req.method === 'POST' || req.url.includes('/save') || req.url.includes('/add')) {
                toastr.success('Saved successfully');
              } else if (req.method === 'PUT' || req.url.includes('/update')) {
                toastr.success('Updated successfully');
              }
            }
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        if (!req.context.get(BYPASS_TOASTER)) {
          console.error('API Error details:', error);
          const errorMsg = error.error?.msg || error.error?.message || error.message || 'Something went wrong, please try again';
          toastr.error(errorMsg);
        }
      }
    }),
    finalize(() => {
      if (!isBackgroundCall) {
        activeRequestsCount = Math.max(0, activeRequestsCount - 1);
        if (activeRequestsCount === 0) {
          // Clear the safety timeout since we're hiding normally
          if (safetyTimeoutId) {
            clearTimeout(safetyTimeoutId);
            safetyTimeoutId = null;
          }
          // Use zone.run to ensure Angular change detection picks up the hide
          zone.run(() => {
            spinner.hide();
          });

          // Extra safety: ensure DOM overlay is hidden after spinner.hide()
          setTimeout(() => {
            const overlays = document.querySelectorAll('ngx-spinner .overlay.show');
            if (overlays.length > 0 && activeRequestsCount === 0) {
              overlays.forEach((el: Element) => {
                (el as HTMLElement).classList.remove('show');
                (el as HTMLElement).style.display = 'none';
                (el as HTMLElement).style.pointerEvents = 'none';
              });
            }
          }, 300);
        }
      }
    })
  );
};

